import React, { useState, useRef, useEffect } from "react";
import { useHistory } from "react-router-dom";
import "./bannerslider.css";

const BannerSlider = ({ banners = [], type = "large", children }) => {
  const history = useHistory();
  const [activeIndex, setActiveIndex] = useState(0);
  const scrollRef = useRef(null);

  // Drag variables
  const isDragging = useRef(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);
  const rafId = useRef(null);

  const handleClick = (link, buttonShow) => {
    if (!!buttonShow) return;

    if (link?.startsWith("http")) {
      const newWindow = window.open(link, "_blank", "noopener,noreferrer");
      if (newWindow) newWindow.opener = null;
    } else if (link) {
      history.push(link);
    }
  };

  // Check if the banner URL is a video or GIF
  const isVideoFormat = (url = "") => {
    const videoExtensions = [".mp4", ".webm", ".ogg"];
    const gifExtensions = [".gif"];
    const lower = url.toLowerCase();
    return (
      videoExtensions.some((ext) => lower.endsWith(ext)) ||
      gifExtensions.some((ext) => lower.endsWith(ext))
    );
  };

  const onDragStart = (e) => {
    isDragging.current = true;
    startX.current = e.pageX || e.touches[0].pageX;
    scrollLeft.current = scrollRef.current.scrollLeft;
  };

  const onDragEnd = () => {
    isDragging.current = false;
  };

  const onDragMove = (e) => {
    if (!isDragging.current) return;
    e.preventDefault();
    const x = e.pageX || e.touches[0].pageX;
    const walk = (startX.current - x) * 1.5;
    scrollRef.current.scrollLeft = scrollLeft.current + walk;
  };

  const onScroll = () => {
    if (rafId.current) cancelAnimationFrame(rafId.current);
    rafId.current = requestAnimationFrame(() => {
      const scrollPosition = scrollRef.current.scrollLeft;
      const width = scrollRef.current.clientWidth;
      const index = Math.round(scrollPosition / width);
      setActiveIndex(index);
    });
  };

  // Auto-scroll effect
  useEffect(() => {
    if (!scrollRef.current || banners?.length <= 1) return;
    const interval = setInterval(() => {
      const next = (activeIndex + 1) % banners.length;
      scrollRef.current.scrollTo({
        left: next * scrollRef.current.clientWidth,
        behavior: "smooth",
      });
      setActiveIndex(next);
    }, 5000);
    return () => clearInterval(interval);
  }, [activeIndex, banners]);

  const renderBannerContent = (banner) => {
    if (!banner) return null;

    return (
      <div className="sl-banner-content">
        {banner.title && <h2 className="sl-banner-title">{banner.title}</h2>}
        {banner.subtitle && (
          <p className="sl-banner-subtitle">{banner.subtitle}</p>
        )}
        {banner.buttonShow && banner.buttonText && (
          <button
            className="btn btn-secondary-v1"
            style={{
              padding: "4px 16px",
              width: "fit-content",
              minWidth: "unset",
              position: "absolute",
              right: 25,
              top: 25,
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleClick(banner.redirectUrl, false);
            }}
          >
            {banner.buttonText}
          </button>
        )}
      </div>
    );
  };

  const renderBannerMedia = (banner) => {
    const url = banner?.imageUrl;
    const isVideo = isVideoFormat(url);

    if (isVideo) {
      // GIFs autoplay naturally, but for .mp4 etc. use <video>
      if (url.endsWith(".gif")) {
        return (
          <img
            src={url}
            alt={banner.title || "banner"}
            className="sl-banner-media"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              pointerEvents: "none",
              userSelect: "none",
            }}
          />
        );
      }
      return (
        <div
          style={{
            width: "100%",
            height: "100%",
            borderRadius: "12px",
            overflow: "hidden",
          }}
        >
          <video
            className="sl-banner-media"
            src={url}
            autoPlay
            muted
            loop
            playsInline
            style={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
              display: "block",
              pointerEvents: "none",
              userSelect: "none",
            }}
          />
        </div>
      );
    }

    // Image fallback
    return (
      <div
        className="sl-banner-bg"
        style={{
          backgroundImage: `url(${url})`,
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
          width: "100%",
          height: "100%",
        }}
      />
    );
  };

  return (
    <div className={`sl-banner-slider ${type}`}>
      {banners.length === 1 ? (
        <div
          className={`sl-banner-single banner-${type}`}
          onClick={() =>
            handleClick(banners[0]?.redirectUrl, banners[0]?.buttonShow)
          }
          style={{ position: "relative", overflow: "hidden" }}
        >
          {renderBannerMedia(banners[0])}
          {children
            ? React.cloneElement(children, { ...banners[0] })
            : renderBannerContent(banners[0])}
        </div>
      ) : (
        <div
          className="sl-banner-scroll"
          ref={scrollRef}
          onMouseDown={onDragStart}
          onTouchStart={onDragStart}
          onMouseUp={onDragEnd}
          onTouchEnd={onDragEnd}
          onMouseLeave={onDragEnd}
          onTouchCancel={onDragEnd}
          onMouseMove={onDragMove}
          onTouchMove={onDragMove}
          onScroll={onScroll}
          style={{
            overflowX: "auto",
            scrollSnapType: "x mandatory",
            display: "flex",
            scrollBehavior: "smooth",
            WebkitOverflowScrolling: "touch",
            cursor: isDragging.current ? "grabbing" : "grab",
          }}
        >
          {banners.map((banner, index) => (
            <div
              key={index}
              className={`sl-banner-item banner-${type}`}
              onClick={() =>
                handleClick(banner?.redirectUrl, banner?.buttonShow)
              }
              style={{
                flex: "0 0 100%",
                scrollSnapAlign: "start",
                userSelect: "none",
                position: "relative",
                overflow: "hidden",
              }}
            >
              {renderBannerMedia(banner)}
              {children
                ? React.cloneElement(children, { ...banner })
                : renderBannerContent(banner)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BannerSlider;
